import { ChangeDetectorRef, Component, ComponentFactoryResolver, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { ConfigService, DataTablesService, GuidService } from '../../../../../core';
import { DecimalFormatPipe } from '../../../../../shared';
import { ConfigPedido } from '../../../../configuracoes/app/pedido';
import { PedidoItemDRPView } from './pedido-item-drp-view';
import { ABRIR_DETALHADO, CampoNumericoDRP, LinhaDRP, LINHAS_DRP, temComponentes } from '../../drp-linhas';
import { EscalaRentabilidadeComponent } from '../../../../../domains/core/settings/grupos-rentabilidade/components/escala-rentabilidade/escala-rentabilidade.component';


interface Config {
    /** Se pode visualizar as informações de Qualidade de Valor (QV) */
    canShowQV: boolean;
    /** Se pode visualizar as informações de D.R.P */
    canShowDRP: boolean;
    /** Se pode visualizar as informações de D.R.P Custos */
    canShowDRPCustos: boolean;
}

/** Quantas colunas fixas (item, quantidade, descontos) antecedem os grupos do D.R.P */
const COLUNAS_FIXAS = 9;

@Component({
    selector: 'sgt-pedido-item-drp-view',
    templateUrl: './pedido-item-drp-view.component.html',
    styleUrls: ['./pedido-item-drp-view.component.less']
})
export class PedidoItemDRPViewComponent implements OnInit, OnChanges, OnDestroy {
    config: Config = {
        canShowQV: false,
        canShowDRP: false,
        canShowDRPCustos: false
    };
    configPedido: ConfigPedido;
    idTable: string;
    dataTable: any;

    /** Enquanto false, o <table> sai do DOM para ser recriado do zero na troca de modo */
    tabelaVisivel = true;
    /** Linhas do D.R.P visíveis no modo e nas permissões atuais, cada uma um grupo de colunas */
    grupos: LinhaDRP[] = [];

    @Input() pedidoItens: PedidoItemDRPView[];
    /** Se a tabela mostra os componentes do D.R.P ou apenas as etapas da cascata */
    @Input() detalhado = ABRIR_DETALHADO;

    /** Container para criar dinamicamente de badges para a datatable */
    @ViewChild('badgesContainer', {read: ViewContainerRef}) badgesContainer: ViewContainerRef;

    constructor(private componentFactoryResolver: ComponentFactoryResolver,
                private renderer: Renderer2,
                private authService: AuthService,
                private configService: ConfigService,
                private decimalFormatPipe: DecimalFormatPipe,
                private dataTablesService: DataTablesService,
                private guidService: GuidService,
                private changeDetectorRef: ChangeDetectorRef) { }

    ngOnInit() {
        this.setConfig();
        this.configPedido = this.configService.getPedido();
        this.idTable = `pedidoItemDRPViewTable_${this.guidService.generate()}`;
        this.montarGrupos();
        this.showTable();
    }

    /** Quantas colunas a tabela tem no modo atual, contando as fixas */
    get totalColunas(): number {
        const fixas = COLUNAS_FIXAS - (this.config.canShowQV ? 0 : 1) - (this.configPedido?.exibirPercDesconto2 ? 0 : 1);
        return fixas + this.grupos.reduce((total, grupo) => total + (grupo.campoPerc ? 2 : 1), 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.detalhado && !changes.detalhado.firstChange) {
            this.montarGrupos();
            this.remontarTabela();
        }
    }

    /**
     * Recria a table do zero.
     *
     * Enquanto a instância do DataTables está viva ela é dona do <thead>, e deixar o *ngFor trocar os
     * <th> por baixo dela deixa o cabeçalho com colunas órfãs e desalinhado do corpo. Por isso a
     * table sai do DOM e volta já com o cabeçalho do modo atual, antes de a DataTable ser montada.
     */
    private remontarTabela() {
        this.dataTablesService.destroyTable(this.dataTable);
        this.dataTable = null;

        this.tabelaVisivel = false;
        this.changeDetectorRef.detectChanges();

        this.tabelaVisivel = true;
        this.changeDetectorRef.detectChanges();

        this.showTable();
    }

    /** Atualiza a table do DRP dos itens do pedido */
    refresh() {
        this.showTable();
    }

    ngOnDestroy() {
        this.badgesContainer?.clear();
        if (this.dataTable) {
            this.dataTable.destroy(true);
        }
    }

    /** Configura as permissões de visualização */
    private setConfig() {
        this.config = {
            canShowQV: this.authService.podeVisualizarQV(),
            canShowDRP: this.authService.podeVisualizarDRP(),
            canShowDRPCustos: this.authService.podeVisualizarDRPCustos()
        };
    }

    /** Seleciona as linhas conforme o modo atual e as permissões do usuário */
    private montarGrupos() {
        this.grupos = LINHAS_DRP.filter((linha, indice) => this.podeExibirLinha(linha) && this.linhaEstaNoModo(linha, indice));
    }

    private podeExibirLinha(linha: LinhaDRP): boolean {
        if (linha.permissao === 'custos') {
            return this.config.canShowDRPCustos;
        }
        if (linha.permissao === 'drp') {
            return this.config.canShowDRP;
        }
        return true;
    }

    /**
     * Na horizontal não há como recuar um componente sob a sua etapa, então os dois não convivem: no
     * modo detalhado a etapa dá lugar aos componentes que a formam, e no resumido acontece o inverso.
     */
    private linhaEstaNoModo(linha: LinhaDRP, indice: number): boolean {
        if (linha.nivel === 'componente') {
            return this.detalhado;
        }
        return !this.detalhado || !temComponentes(indice);
    }

    /** Monta a coluna de um valor ou percentual do D.R.P */
    private colunaDRP(className: string, campo: CampoNumericoDRP) {
        return {
            data: null,
            className: `${className} text-right`,
            render: (pedidoItem: PedidoItemDRPView) => {
                if (pedidoItem && pedidoItem.drp) {
                    return this.decimalFormatPipe.transform(pedidoItem.drp[campo], 1, 2, 2);
                }
                return '';
            }
        };
    }

    /** Monta as colunas fixas — as que não vêm do D.R.P */
    private colunasFixas() {
        const colunas: any[] = [
            {
                data: null,
                className: 'col-codigo-item',
                render: (pedidoItem: PedidoItemDRPView) => {
                    if (pedidoItem && pedidoItem.item) {
                        return pedidoItem.item.codigoIntegracaoErp;
                    }
                    return '';
                },
                createdCell: (td: any, cellData: any, pedidoItem: PedidoItemDRPView) => {
                    if (pedidoItem && pedidoItem.item) {
                        td.title = pedidoItem.item.codigoIntegracaoErp;
                    }
                }
            },
            {
                data: null,
                className: 'col-item',
                render: (pedidoItem: PedidoItemDRPView) => {
                    if (pedidoItem && pedidoItem.item) {
                        return pedidoItem.item.nome;
                    }
                    return '';
                },
                createdCell: (td: any, cellData: any, pedidoItem: PedidoItemDRPView) => {
                    if (pedidoItem && pedidoItem.item) {
                        td.title = pedidoItem.item.nome;
                    }
                }
            }
        ];

        if (this.config.canShowQV) {
            colunas.push({
                data: null,
                className: 'col-qv-grupo-rentabilidade text-left',
                render: (pedidoItem: PedidoItemDRPView, type: string) => {
                    if (type === 'sort') {
                        return pedidoItem?.drp?.escalaGrupoRentabilidade ?? null;
                    }
                    return '';
                },
                createdCell: (td: any, cellData: any, pedidoItem: PedidoItemDRPView) => {
                    if (pedidoItem && pedidoItem.drp) {
                        const badgeFactory = this.componentFactoryResolver.resolveComponentFactory(EscalaRentabilidadeComponent);
                        const badgeRef = this.badgesContainer.createComponent(badgeFactory);
                        badgeRef.instance.id = pedidoItem.drp.escalaGrupoRentabilidade;
                        badgeRef.instance.showText = true;
                        this.renderer.appendChild(td, badgeRef.location.nativeElement);
                    }
                }
            });
        }

        colunas.push(
            {
                data: 'valorUnitario',
                className: 'col-valor-unitario text-right',
                render: (data: number) => this.decimalFormatPipe.transform(data, 1, 4, 4)
            },
            {
                data: 'quantidade',
                className: 'col-quantidade text-right',
                render: (data: number) => this.decimalFormatPipe.transform(data, 1, 3, 3)
            },
            {
                data: 'percDesconto1',
                className: 'col-perc-desc-1 text-right',
                render: (data: number) => this.decimalFormatPipe.transform(data, 1, 2, 2)
            }
        );

        if (this.configPedido?.exibirPercDesconto2) {
            colunas.push({
                data: 'percDesconto2',
                className: 'col-perc-desc-2 text-right',
                render: (data: number) => this.decimalFormatPipe.transform(data, 1, 2, 2)
            });
        }

        colunas.push(
            {
                data: null,
                className: 'col-perc-desc-avista text-right',
                render: () => ''
            },
            {
                data: 'valorLiquido',
                className: 'col-valor-liquido text-right',
                render: (data: number) => this.decimalFormatPipe.transform(data, 1, 4, 4)
            }
        );

        return colunas;
    }

    private showTable() {
        // As badges de QV são recriadas a cada montagem da table
        this.badgesContainer?.clear();

        const colunas = [...this.colunasFixas()];
        this.grupos.forEach(grupo => {
            colunas.push(this.colunaDRP(`col-drp-valor-${grupo.slug}`, grupo.campoValor));
            if (grupo.campoPerc) {
                colunas.push(this.colunaDRP(`col-drp-perc-${grupo.slug}`, grupo.campoPerc));
            }
        });

        // Ordenação: pela margem de lucro quando visível, senão pelo QV, senão pelo código do item
        const indice = (classe: string) => colunas.findIndex(coluna => coluna.className.startsWith(classe));
        let orderByColumn = 0; // Código do item
        let orderByDir = 'asc';
        if (indice('col-qv-grupo-rentabilidade') >= 0) {
            orderByColumn = indice('col-qv-grupo-rentabilidade');
            orderByDir = 'desc';
        }
        if (indice('col-drp-perc-lucro-liquido') >= 0) {
            orderByColumn = indice('col-drp-perc-lucro-liquido');
            orderByDir = 'desc';
        }

        this.dataTablesService.showTable(`#${this.idTable}`, this.dataTable, {
            data: this.pedidoItens,
            columns: colunas,
            order: [[orderByColumn, orderByDir]],
            columnDefs: [
                {orderable: true, targets: 0}
            ],
            paging: false,
            searching: false,
            info: false,
            scroll: false,
            createdRow(row, pedidoItem: PedidoItemDRPView) {
                // Adicionando class css nos rows da tabel
                if (pedidoItem && pedidoItem.drp) {
                    if (pedidoItem.drp.percLucroLiquidoAbaixo) {
                        jQuery(row).addClass('row-pedido-lucro-liquido-abaixo');
                    }
                }
            }
        }).then(dataTable => {
            this.dataTable = dataTable;
        });
    }
}
